import DOMPurify from "dompurify";

// Rich text of descriptions, reports and notes (boss's task «تغییر نمایش فیلد های نوشتن متنی», 2026-10-24;
// docs/tasks/25-rich-text-editor.md). The editor saves a small HTML subset; texts saved before stay plain text and are
// shown with their line breaks. Everything shown goes through DOMPurify with this allow-list.

export const RICH_TAGS = ["p", "br", "strong", "b", "em", "i", "u", "s", "ol", "ul", "li", "a"];
const RICH_ATTRS = ["href", "target", "rel"];
const RICH_PATTERN = /<\/?(p|br|strong|b|em|i|u|s|ol|ul|li|a)(\s[^>]*)?\/?>/i;

let hooked = false;
function ensureLinkHook() {
    if (hooked) return;
    hooked = true;
    // links open in a new tab and only with safe protocols
    DOMPurify.addHook("afterSanitizeAttributes", (node) => {
        if (node.tagName === "A") {
            const href = node.getAttribute("href") || "";
            if (!/^(https?:|mailto:|tel:|#|\/)/i.test(href)) node.removeAttribute("href");
            node.setAttribute("target", "_blank");
            node.setAttribute("rel", "noopener noreferrer");
        }
    });
}

export const isRichText = (value) => typeof value === "string" && RICH_PATTERN.test(value);

export function sanitizeRichText(html) {
    ensureLinkHook();
    return DOMPurify.sanitize(String(html || ""), {ALLOWED_TAGS: RICH_TAGS, ALLOWED_ATTR: RICH_ATTRS});
}

const escapeHtml = (text) => String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// an old plain-text value as editor HTML: one paragraph per line
export function plainToHtml(text) {
    const value = String(text ?? "");
    if (!value.trim()) return "";
    return value.split(/\r?\n/).map((line) => (line.trim() ? `<p>${escapeHtml(line)}</p>` : "<p><br></p>")).join("");
}

// Quill 2 saves a blank line (Enter on an empty line) as "<p></p>", which shows as nothing; a blank line is kept as
// "<p><br></p>" so it shows, and texts saved before this fix open with their blank lines too
export const keepBlankLines = (html) => String(html || "").replace(/<p>\s*<\/p>/gi, "<p><br></p>");

export const toEditorHtml = (value) => (isRichText(value) ? keepBlankLines(sanitizeRichText(value)) : plainToHtml(value));

// readable plain text (previews, cards, search, AI prompts): blocks and list items on their own lines
export function richTextToPlain(value) {
    if (value === null || value === undefined) return "";
    const text = String(value);
    if (!isRichText(text)) return text;
    const withBreaks = text
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/(p|li)>/gi, "\n")
        .replace(/<li[^>]*>/gi, "• ");
    const holder = document.createElement("div");
    holder.innerHTML = sanitizeRichText(withBreaks);
    return (holder.textContent || "")
        .replace(/\u00a0/g, " ")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

// an editor with nothing written in it is an empty value, not «<p><br></p>». Quill 2.0.3's getSemanticHTML turns every
// space into &nbsp; (lines would not wrap), so they become normal spaces again.
export function normalizeEditorHtml(html) {
    const value = keepBlankLines(String(html || "").replace(/&nbsp;/g, " "));
    const holder = document.createElement("div");
    holder.innerHTML = value;
    return (holder.textContent || "").replace(/\u00a0/g, " ").trim() ? value : "";
}

export const isBlankRichText = (value) => !richTextToPlain(value).trim();
